import { articleApi, authApi } from './supabaseApi';
import { mockApi } from './mockData';
import { checkSupabaseConfig } from '../lib/supabase';

// 检查是否使用模拟数据
const useMockData = !checkSupabaseConfig();

// 文章相关 API
export const fetchArticles = async () => {
    if (useMockData) {
        return await mockApi.getArticles();
    }

    const maxRetries = 3;
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            const articles = await articleApi.getArticles();
            return articles;
        } catch (error: any) {
            lastError = error;

            if (attempt < maxRetries) {
                // 等待一段时间后重试
                await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            }
        }
    }

    throw new Error(`获取文章失败，已重试${maxRetries}次: ${lastError?.message || '未知错误'}`);
};

export const fetchArticleDetail = async (id: number) => {
    if (useMockData) {
        const article = await mockApi.getArticle(id);
        if (!article) {
            throw new Error('文章不存在');
        }
        return article;
    }

    const article = await articleApi.getArticle(id);
    if (!article) {
        throw new Error('文章不存在');
    }
    return article;
};

export const publishArticle = async (articleData: {
    title: string;
    summary: string;
    content: string;
    coverImage?: string;
}) => {
    if (useMockData) {
        throw new Error('模拟模式下不支持发布文章');
    }

    const currentUser = await authApi.getCurrentUser();
    if (!currentUser) {
        throw new Error('未登录');
    }

    const article = await articleApi.createArticle({
        title: articleData.title,
        author: currentUser.username,
        date: new Date().toISOString(),
        summary: articleData.summary,
        content: articleData.content,
        cover_image: articleData.coverImage
    });

    return { message: '文章发布成功', article };
};

// 用户认证相关 API
export const loginUser = async (data: { username: string; password: string }) => {
    if (useMockData) {
        return await mockApi.login(data.username, data.password);
    }

    const result = await authApi.login(data.username, data.password);
    return {
        token: result.session?.access_token,
        username: data.username
    };
};

export const registerUser = async (data: { username: string; password: string; avatar: string }) => {
    if (useMockData) {
        return await mockApi.register(data.username, data.password);
    }

    const result = await authApi.register(data.username, data.password, data.avatar);
    return result;
};

export const getCurrentUser = async () => {
    if (useMockData) {
        // mock 也返回 avatar_url 字段，保持类型一致
        const user = await mockApi.getCurrentUser();
        return { username: user.username, avatar_url: user.avatar_url || null };
    }

    const user = await authApi.getCurrentUser();
    if (!user) {
        throw new Error('未登录');
    }
    return { username: user.username, avatar_url: user.avatar_url || null };
};

export const logoutUser = async () => {
    if (useMockData) {
        // 模拟登出
        return;
    }

    await authApi.logout();
};